import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useDashboardTheme } from "../../../../context/DashboardThemeContext";
import { useAlert } from "../../../../context/AlertContext";

const fields = [
  ["primary_color", "Color principal"],
  ["button_color", "Color de botones"],
  ["button_text_color", "Texto de botones"],
  ["table_header_color", "Encabezado de tablas"],
  ["table_header_text_color", "Texto encabezado de tablas"],
  ["sidebar_color", "Color del sidebar"],
  ["sidebar_text_color", "Texto del sidebar"],
];

export default function ThemeSettings() {
  const {
    dashboardTheme,
    storedTheme,
    updateDashboardTheme,
    resetDashboardTheme,
  } = useDashboardTheme();

  const { showSuccess, showError } = useAlert();

  const [form, setForm] = useState(storedTheme);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(storedTheme);
  }, [storedTheme]);

  const handleChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);

    const result = await updateDashboardTheme({
      primary_color: form.primary_color,
      button_color: form.button_color,
      button_text_color: form.button_text_color,
      table_header_color: form.table_header_color,
      table_header_text_color: form.table_header_text_color,
      sidebar_color: form.sidebar_color,
      sidebar_text_color: form.sidebar_text_color,
    });

    if (result.ok) {
      showSuccess("Configuración guardada correctamente.");
    } else {
      showError("No se pudo guardar la configuración.");
    }

    setSaving(false);
  };

  const handleReset = async () => {
    setSaving(true);

    const result = await resetDashboardTheme();

    if (result.ok) {
      showSuccess("Colores restaurados correctamente.");
    } else {
      showError("No se pudieron restaurar los colores.");
    }

    setSaving(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: "24px",
        border: `1px solid ${dashboardTheme.border_color}`,
        bgcolor: dashboardTheme.card_background,
        p: { xs: 2.5, md: 3 },
      }}
    >
      <Stack spacing={2.5}>
        <Box>
          <Typography
            sx={{
              fontSize: { xs: 24, md: 30 },
              fontWeight: 600,
              color: dashboardTheme.text_color,
              lineHeight: 1.2,
            }}
          >
            Configuración visual
          </Typography>

          <Typography
            sx={{
              mt: 0.7,
              fontSize: 14,
              fontWeight: 400,
              color: dashboardTheme.muted_text_color,
              lineHeight: 1.6,
            }}
          >
            Personaliza los colores principales del panel. El modo claro u oscuro se cambia desde la barra superior.
          </Typography>
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(3, 1fr)",
            },
            gap: 2,
          }}
        >
          {fields.map(([field, label]) => (
            <Stack key={field} spacing={0.8}>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: dashboardTheme.text_color,
                }}
              >
                {label}
              </Typography>

              <Stack direction="row" spacing={1}>
                <TextField
                  fullWidth
                  value={form[field] || ""}
                  onChange={(event) => handleChange(field, event.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      bgcolor: dashboardTheme.input_background,
                      color: dashboardTheme.text_color,
                    },
                    "& input": {
                      color: dashboardTheme.text_color,
                    },
                  }}
                />

                <Box
                  component="input"
                  type="color"
                  value={form[field] || "#000000"}
                  onChange={(event) => handleChange(field, event.target.value)}
                  sx={{
                    width: 48,
                    minWidth: 48,
                    height: 40,
                    border: `1px solid ${dashboardTheme.border_color}`,
                    borderRadius: "12px",
                    bgcolor: "transparent",
                    p: 0.5,
                    cursor: "pointer",
                  }}
                />
              </Stack>
            </Stack>
          ))}
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
          <Button
            disabled={saving}
            onClick={handleSave}
            sx={{
              minHeight: 42,
              px: 2.5,
              borderRadius: "14px",
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              bgcolor: dashboardTheme.button_color,
              color: dashboardTheme.button_text_color,
              boxShadow: "none",
              "&:hover": {
                bgcolor: dashboardTheme.button_color,
                boxShadow: "none",
              },
            }}
          >
            Guardar cambios
          </Button>

          <Button
            disabled={saving}
            onClick={handleReset}
            sx={{
              minHeight: 42,
              px: 2.5,
              borderRadius: "14px",
              textTransform: "none",
              fontSize: 14,
              fontWeight: 600,
              color: dashboardTheme.text_color,
              border: `1px solid ${dashboardTheme.border_color}`,
              boxShadow: "none",
              "&:hover": {
                bgcolor: `${dashboardTheme.primary_color}18`,
                boxShadow: "none",
              },
            }}
          >
            Restaurar colores por defecto
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}